import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  pricingTiers,
  getTierFeatures,
  getButtonText,
} from '../utils/pricingConfig';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const handleStartInterview = () => {
    navigate('/practice');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  return (
    <div className='overflow-y-auto h-[100vh] bg-gray-50'>
      {/* Hero Section */}
      <div className='bg-gradient-to-br from-blue-600 to-blue-800 text-white'>
        <div className='max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8'>
          <div className='text-center'>
            <h1 className='text-4xl sm:text-6xl font-bold mb-6'>
              Ace Your <span className='text-yellow-400'>Interview</span>
            </h1>
            <p className='text-xl sm:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed'>
              Practice{' '}
              <span className='underline decoration-3 decoration-yellow-400'>
                speaking out loud
              </span>{' '}
              with AI-powered mock interviews. <br></br> No appointments needed
              - just real conversations, real feedback, anytime.
            </p>
            <div className='flex flex-col sm:flex-row gap-4 justify-center'>
              <button
                onClick={handleStartInterview}
                className='bg-yellow-400 text-black px-8 py-4 text-lg font-semibold rounded-lg hover:bg-blue-50 hover:-translate-y-1 hover:shadow-lg transition-all duration-200'
              >
                Start Free Practice
              </button>
              <button
                onClick={handleLogin}
                className='border-2 border-white text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-200'
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Current Focus Section */}
      <div className='py-16 bg-blue-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-12'>
            <h2 className='text-3xl sm:text-4xl font-bold text-gray-900 mb-4'>
              🚀 Now Available: Coding Interviews
            </h2>
            <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
              Start with our flagship offering - AI-powered coding interview
              practice. More interview types coming soon!
            </p>
          </div>
          <div className='bg-white rounded-2xl shadow-xl p-8 md:p-12'>
            <div className='grid md:grid-cols-3 gap-8 items-start'>
              {/* Available Now - Main Section */}
              <div className='md:col-span-2'>
                <div className='flex items-center mb-6'>
                  <span className='bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium mr-4'>
                    ✓ Available Now
                  </span>
                  <h3 className='text-2xl font-bold text-gray-900'>
                    Coding Interviews
                  </h3>
                </div>
                <p className='text-gray-600 mb-6'>
                  Master technical interviews through conversation - speak your
                  solution while you code, just like real interviews.
                </p>
                <ul className='space-y-4 text-gray-700 mb-8'>
                  <li className='flex items-start'>
                    <svg
                      className='w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                    <span>
                      <strong>Speak your solution:</strong> Explain your
                      approach out loud, just like in real interviews
                    </span>
                  </li>
                  <li className='flex items-start'>
                    <svg
                      className='w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                    <span>
                      <strong>Interactive coding:</strong> Write code while
                      discussing your thought process with the AI
                    </span>
                  </li>
                  <li className='flex items-start'>
                    <svg
                      className='w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                    <span>
                      <strong>Real-time guidance:</strong> Get hints and
                      feedback during the interview, not just after
                    </span>
                  </li>
                  <li className='flex items-start'>
                    <svg
                      className='w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                    <span>
                      <strong>Detailed analysis:</strong> Get transcript recaps,
                      code optimization tips, and communication insights
                    </span>
                  </li>
                </ul>
                <button
                  onClick={handleStartInterview}
                  className='bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors'
                >
                  Start Coding Interview →
                </button>
              </div>

              {/* Coming Soon - Sidebar */}
              <div className='text-center'>
                <div className='bg-gray-50 border border-gray-200 p-4 rounded-xl'>
                  <div className='text-lg mb-2'>🚧</div>
                  <h4 className='text-sm font-medium text-gray-600 mb-2'>
                    Coming Soon
                  </h4>
                  <div className='space-y-1 text-gray-500 text-xs'>
                    <div>Behavioral Interviews</div>
                    <div>System Design</div>
                    <div>& More</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className='py-16 bg-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-16'>
            <h2 className='text-3xl sm:text-4xl font-bold text-gray-900 mb-4'>
              Why Choose Our Platform?
            </h2>
            <p className='text-xl text-gray-600 max-w-2xl mx-auto'>
              Everything you need to ace any interview with conversational AI
              practice sessions
            </p>
          </div>
          <div className='grid md:grid-cols-3 gap-8'>
            <div className='text-center p-6'>
              <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6'>
                <svg
                  className='w-8 h-8 text-blue-600'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm4 2a1 1 0 000 2h6a1 1 0 100-2H7z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                Speak Out Loud
              </h3>
              <p className='text-gray-600'>
                Practice speaking your thoughts out loud, just like a real
                interview - no scripts, just natural conversation with AI.
              </p>
            </div>
            <div className='text-center p-6'>
              <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6'>
                <svg
                  className='w-8 h-8 text-blue-600'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.98 5.98 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-1.588-1.588A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                No Appointments
              </h3>
              <p className='text-gray-600'>
                Practice anytime, anywhere. No scheduling, no waiting - instant
                access to AI interviewers 24/7
              </p>
            </div>
            <div className='text-center p-6'>
              <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6'>
                <svg
                  className='w-8 h-8 text-blue-600'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                Real Feedback
              </h3>
              <p className='text-gray-600'>
                Get immediate, detailed feedback on your technical,
                communication, and performance after each session
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className='py-16 bg-gray-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-16'>
            <h2 className='text-3xl sm:text-4xl font-bold text-gray-900 mb-4'>
              How It Works
            </h2>
            <p className='text-xl text-gray-600 max-w-2xl mx-auto'>
              Experience real interview conversations - no scripts, no rigid
              structure, just natural dialogue
            </p>
          </div>
          <div className='grid md:grid-cols-3 gap-8'>
            <div className='bg-white p-8 rounded-xl shadow-lg text-center'>
              <div className='w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold'>
                1
              </div>
              <h3 className='text-xl font-semibold text-gray-900 mb-4'>
                Start Speaking
              </h3>
              <p className='text-gray-600 mb-6'>
                Choose your interview type and difficulty. No preparation needed
                - just start talking through the problem like you would with a
                real interviewer.
              </p>
              <div className='text-sm text-gray-500'>
                • Instant start <br></br>• No scheduling <br></br>• Multiple
                difficulty levels
              </div>
            </div>
            <div className='bg-white p-8 rounded-xl shadow-lg text-center'>
              <div className='w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold'>
                2
              </div>
              <h3 className='text-xl font-semibold text-gray-900 mb-4'>
                Have a Conversation
              </h3>
              <p className='text-gray-600 mb-6'>
                Speak your thoughts out loud as you work through the problem.
                The AI responds naturally, asks follow-up questions, and
                provides guidance.
              </p>
              <div className='text-sm text-gray-500'>
                • Natural dialogue <br></br>• Real-time feedback <br></br>•
                Interactive guidance
              </div>
            </div>
            <div className='bg-white p-8 rounded-xl shadow-lg text-center'>
              <div className='w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold'>
                3
              </div>
              <h3 className='text-xl font-semibold text-gray-900 mb-4'>
                Learn & Improve
              </h3>
              <p className='text-gray-600 mb-6'>
                Get detailed analysis of your communication style,
                problem-solving approach, and technical accuracy with specific
                improvement suggestions.
              </p>
              <div className='text-sm text-gray-500'>
                • Communication analysis <br></br>• Performance scores <br></br>
                • Improvement hightlights
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className='py-16 bg-white'>
        <div className='max-w-8xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-16'>
            <h2 className='text-3xl sm:text-4xl font-bold text-gray-900 mb-4'>
              Choose Your Plan
            </h2>
            <p className='text-xl text-gray-600 max-w-2xl mx-auto'>
              Start free and upgrade as you progress. All plans include
              AI-powered feedback and detailed analysis.
            </p>
          </div>
          <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-8'>
            {pricingTiers.map((tier, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-lg border-2 transition-all duration-200 hover:shadow-xl flex flex-col h-full ${
                  tier.popular ? 'border-blue-500 relative' : 'border-gray-200'
                }`}
              >
                {tier.popular && (
                  <div className='absolute -top-3 left-1/2 transform -translate-x-1/2'>
                    <span className='bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold'>
                      Most Popular
                    </span>
                  </div>
                )}
                <div className='p-8 flex flex-col flex-grow'>
                  <h3 className='text-2xl font-bold text-gray-900 mb-2'>
                    {tier.name}
                  </h3>
                  <div className='mb-6'>
                    <span className='text-4xl font-bold text-gray-900'>
                      {tier.price}
                    </span>
                    <span className='text-gray-600'>{tier.period}</span>
                  </div>
                  <div className='mb-6'>
                    <div className='text-lg font-semibold text-blue-600 mb-2'>
                      {tier.minutes} minutes/month
                    </div>
                  </div>
                  <ul className='space-y-3 mb-8 flex-grow'>
                    {getTierFeatures(tier).map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className='flex items-start'
                      >
                        <svg
                          className='w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                        >
                          <path
                            fillRule='evenodd'
                            d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                            clipRule='evenodd'
                          />
                        </svg>
                        <span className='text-gray-600'>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={handleSignUp}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors mt-auto ${
                      tier.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {getButtonText(tier, 'landing')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className='py-16 bg-gradient-to-r from-blue-600 to-purple-700 text-white'>
        <div className='max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8'>
          <h2 className='text-3xl sm:text-4xl font-bold mb-8'>
            Ready to Land Your Dream Job?
          </h2>
          {/* <p className='text-xl mb-8 max-w-2xl mx-auto'>
            Join thousands of developers who've improved their interview skills
            and landed jobs at top tech companies.
          </p> */}
          <div className='flex flex-col sm:flex-row gap-4 justify-center'>
            <button
              onClick={handleSignUp}
              className='bg-yellow-400 text-gray-900 px-8 py-4 text-lg font-semibold rounded-lg hover:bg-yellow-300 hover:-translate-y-1 hover:shadow-lg transition-all duration-200'
            >
              Start Free Trial
            </button>
            <button
              onClick={handleLogin}
              className='border-2 border-white text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-200'
            >
              Already have an account? Sign In
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='bg-gray-900 text-gray-300 py-8 mb-16'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <p className='text-gray-500'>
            &copy; 2025 StrongHire. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
